import { Task, FullProject, VacationEntry } from '../../types';
import { areProjectIdsMatching, isSystemProject } from '../../utils/idNormalizer';
import { isPurePersonContact, normalizePersonName } from '../../components/communication/personModel';

export const GLOBAL_VIEW_IDS_SET = new Set([
  'dashboard',
  'metricsDashboard',
  'globalKanban',
  'communication',
  'impactEffort',
  'noDateTasks',
  'global',
  'globalCalendar',
  'aiAssistant',
  'analysis',
  'projectPool',
  'globalGantt',
]);

/**
 * Normalizes system project IDs (`inbox_inforum`, `comm_internal`), deduplicates projects by ID
 * while merging tasks/vacations, and applies user-specific project category overrides.
 */
export function normalizeAndDeduplicateProjectList(
  next: FullProject[],
  INBOX_PROJECT_ID: string,
  COMM_PROJECT_ID: string
): FullProject[] {
  const mapped = next.map((p) => {
    if (
      p.id === INBOX_PROJECT_ID ||
      p.id.toLowerCase().startsWith('inbox_inforum') ||
      p.id.toLowerCase() === 'inbox_inforum'
    ) {
      const expectedRawId = p.rawId || (p.id !== 'inbox_inforum' ? p.id : INBOX_PROJECT_ID);
      if (
        p.id === 'inbox_inforum' &&
        p.rawId === expectedRawId &&
        p.name === 'Nicht zugeordnet'
      ) {
        return p;
      }
      return { ...p, id: 'inbox_inforum', rawId: expectedRawId, name: 'Nicht zugeordnet' };
    }
    if (
      p.id === COMM_PROJECT_ID ||
      p.id.toLowerCase().startsWith('comm_internal') ||
      p.id.toLowerCase() === 'comm_internal'
    ) {
      const expectedRawId = p.rawId || (p.id !== 'comm_internal' ? p.id : COMM_PROJECT_ID);
      if (p.id === 'comm_internal' && p.rawId === expectedRawId && p.name === 'Kommunikation') {
        return p;
      }
      return { ...p, id: 'comm_internal', rawId: expectedRawId, name: 'Kommunikation' };
    }
    return p;
  });

  const uniqueMap = new Map<string, FullProject>();
  mapped.forEach((p) => {
    const existing = uniqueMap.get(p.id);
    if (existing) {
      const mergedTasks = [...(existing.tasks || [])];
      (p.tasks || []).forEach((t) => {
        if (!mergedTasks.some((mt) => mt.id === t.id)) {
          mergedTasks.push(t);
        }
      });

      const mergedMeta = { ...(existing.metadata || {}), ...(p.metadata || {}) };
      if (existing.metadata?.vacations || p.metadata?.vacations) {
        const uniqueVacations = new Map<string, VacationEntry>();
        (existing.metadata?.vacations || []).forEach((v) => {
          if (v && v.id) uniqueVacations.set(v.id, v);
        });
        (p.metadata?.vacations || []).forEach((v) => {
          if (v && v.id) uniqueVacations.set(v.id, v);
        });
        mergedMeta.vacations = Array.from(uniqueVacations.values());
      }

      uniqueMap.set(p.id, {
        ...existing,
        rawId: existing.rawId || p.rawId,
        tasks: mergedTasks.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
        metadata: mergedMeta,
        lastModified: Math.max(existing.lastModified || 0, p.lastModified || 0),
      });
    } else {
      uniqueMap.set(p.id, p);
    }
  });

  const deduplicated = Array.from(uniqueMap.values());
  const personalInbox = deduplicated.find((p) => p.id === 'inbox_inforum');
  const userProjCats = personalInbox?.metadata?.projectCategories || {};

  return deduplicated.map((p) => {
    if (p.id !== 'inbox_inforum' && p.id !== 'comm_internal') {
      const nextCategoryId =
        userProjCats[p.id] !== undefined ? userProjCats[p.id] : p.categoryId;
      if (nextCategoryId !== p.categoryId) {
        return {
          ...p,
          categoryId: nextCategoryId,
        };
      }
    }
    return p;
  });
}

/**
 * Prunes unnamed non-inline ghost tasks older than 10 seconds across all projects.
 */
export function pruneUnnamedGhostTasks(currentProjects: FullProject[]): FullProject[] {
  const isGhost = (t: Task) => {
    const isEmpty =
      (!t.name || t.name.trim() === '') && (!t.assignee || t.assignee.trim() === '');
    const isSpecialized =
      t.type === 'contact' || t.type === 'meeting' || t.category === 'Kommunikation';
    const taskTs = typeof t.id === 'string' ? parseInt(t.id, 10) : Number(t.id);
    const isRecent = !isNaN(taskTs) && taskTs > Date.now() - 10000;
    return isEmpty && !isRecent && !isSpecialized && !t.isInlineNew && !t.parentId;
  };

  const hasGhosts = currentProjects.some((p) => (p.tasks || []).some(isGhost));
  if (!hasGhosts) return currentProjects;

  return currentProjects.map((p) => {
    if (!(p.tasks || []).some(isGhost)) return p;
    return {
      ...p,
      tasks: (p.tasks || []).filter((t) => !isGhost(t)),
    };
  });
}

/**
 * Enriches a real project's task list with dated/linked Contact-Vorgänge from other projects.
 */
export function computeProjectTasksWithCommVorgaenge(
  proj: FullProject,
  projectsList: FullProject[]
): Task[] {
  const baseTasks = proj.tasks || [];
  if (isSystemProject(proj.id, proj.name)) return baseTasks;
  const existingIds = new Set(baseTasks.map((t) => String(t.id)));

  const projAssignees = new Set<string>();
  const projContactIds = new Set<string>();
  baseTasks.forEach((t) => {
    if (t.contactId) projContactIds.add(String(t.contactId));
    if (t.assignee) {
      t.assignee.split(/[,;/]+/).forEach((a) => {
        const clean = normalizePersonName(a);
        if (clean && clean !== 'all') projAssignees.add(clean);
      });
    }
  });
  (proj.metadata?.stakeholders || []).forEach((s: { name?: string }) => {
    const clean = normalizePersonName(s?.name);
    if (clean) projAssignees.add(clean);
  });

  const extraCommTasks: Task[] = [];
  projectsList.forEach((sourceProj) => {
    if (sourceProj.id === proj.id || sourceProj.archived) return;
    const isSourceSystem = isSystemProject(sourceProj.id, sourceProj.name);
    (sourceProj.tasks || []).forEach((t) => {
      if (existingIds.has(String(t.id))) return;
      if (isPurePersonContact(t) || t.type === 'contact') return;
      if (t.category === 'Kommunikation' && !t.start && !t.end && isSourceSystem) return;
      const matchesProject =
        (t.projectId && areProjectIdsMatching(t.projectId, proj.id)) ||
        (Array.isArray(t.projectIds) &&
          t.projectIds.some((pid) => areProjectIdsMatching(pid, proj.id)));
      let shouldInclude = matchesProject;
      if (!shouldInclude && isSourceSystem) {
        const hasOtherExplicitProject =
          (t.projectId && !isSystemProject(t.projectId)) ||
          (Array.isArray(t.projectIds) &&
            t.projectIds.some((pid) => pid && !isSystemProject(pid)));
        if (!hasOtherExplicitProject) {
          if (t.contactId && projContactIds.has(String(t.contactId))) shouldInclude = true;
          const norm = normalizePersonName(t.assignee);
          if (norm && projAssignees.has(norm)) shouldInclude = true;
        }
      }
      if (shouldInclude) {
        existingIds.add(String(t.id));
        extraCommTasks.push({
          ...t,
          name:
            t.name?.trim() || (t.assignee ? `Vorgang mit ${t.assignee}` : 'Kontakt-Vorgang'),
          projectId: t.projectId || sourceProj.id,
          projectIds: Array.from(
            new Set([...(t.projectIds || []).filter((pid) => !isSystemProject(pid)), proj.id])
          ),
        });
      }
    });
  });
  return extraCommTasks.length > 0 ? [...baseTasks, ...extraCommTasks] : baseTasks;
}

/**
 * Resolves the currently active project from `allProjects` and `projects`.
 */
export function resolveActiveProjectInstance(params: {
  currentProjectId: string;
  allProjects: FullProject[];
  projects: FullProject[];
  INBOX_PROJECT_ID: string;
  COMM_PROJECT_ID: string;
  getProjectTasksWithCommVorgaenge: (proj: FullProject, projectsList: FullProject[]) => Task[];
}): FullProject {
  const {
    currentProjectId,
    allProjects,
    projects,
    INBOX_PROJECT_ID,
    COMM_PROJECT_ID,
    getProjectTasksWithCommVorgaenge,
  } = params;

  if (GLOBAL_VIEW_IDS_SET.has(currentProjectId)) {
    return {
      id: currentProjectId,
      name: 'Globaler Überblick',
      tasks: [],
      metadata: {},
      changelog: [],
      color: 'bg-blue-500',
      lastModified: 0,
    } as FullProject;
  }

  const project = allProjects.find(
    (p) =>
      p.id === currentProjectId ||
      (currentProjectId === 'inbox_inforum' &&
        (p.id === INBOX_PROJECT_ID || p.id.toLowerCase().startsWith('inbox_inforum_'))) ||
      (currentProjectId === 'comm_internal' &&
        (p.id === COMM_PROJECT_ID || p.id.toLowerCase().startsWith('comm_internal_')))
  );
  if (project) {
    if (project.id === INBOX_PROJECT_ID || project.id.toLowerCase().startsWith('inbox_inforum_')) {
      return { ...project, id: 'inbox_inforum', name: 'Nicht zugeordnet' };
    }
    if (project.id === COMM_PROJECT_ID || project.id.toLowerCase().startsWith('comm_internal_')) {
      return { ...project, id: 'comm_internal', name: 'Kommunikation' };
    }
    const mergedTasks = getProjectTasksWithCommVorgaenge(project, allProjects);
    return mergedTasks !== project.tasks ? { ...project, tasks: mergedTasks } : project;
  }

  const wsProject = projects.find((p) => p.id === currentProjectId);
  if (wsProject) {
    const mergedTasks = getProjectTasksWithCommVorgaenge(wsProject, allProjects);
    return mergedTasks !== wsProject.tasks ? { ...wsProject, tasks: mergedTasks } : wsProject;
  }

  if (projects.length > 0) {
    const firstProj = projects[0];
    const mergedTasks = getProjectTasksWithCommVorgaenge(firstProj, allProjects);
    return mergedTasks !== firstProj.tasks ? { ...firstProj, tasks: mergedTasks } : firstProj;
  }

  return {
    id: 'default',
    name: 'GWE Telfs Projektmanagement',
    tasks: [],
    metadata: {},
    changelog: [],
    color: 'bg-blue-500',
    lastModified: 0,
  } as FullProject;
}
